import {
  Condition,
  ConditionalDataOutput,
  ConditionalStep,
  ConditionDataOutput,
} from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { Badge, Divider, Group, Stack, Text } from '@mantine/core';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import React from 'react';
import { generateUid } from '@/utils/misc.ts';
import ConditionalDataOutputFields from '@/pages/Editor/components/StepSidebar/StepForms/ConditionalStep/ConditionalDataOutputFields.tsx';
import StepFieldsAddButton from '@/pages/Editor/components/StepFieldsAddButton.tsx';
import ConditionFields from '@/pages/Editor/components/StepSidebar/StepForms/ConditionalStep/ConditionFields.tsx';
import { DEFAULT_DYNAMIC_DATA_INPUT, ELSE_CLAUSE_ID } from '@/utils/constants.ts';
import { ConditionAction, InputSource } from '@/types/automationFields.ts';
import { makeDecisionControlLogic } from '@/utils/decisionControlLogic.ts';

type props = {
  step: ConditionalStep;
};

type Fields = {
  data_outputs: ConditionalDataOutput[];
  conditions: Condition[];
};

const updateConditionToMatchOutputs = (
  dataOutputs: ConditionalDataOutput[],
  conditions: Condition[],
): Condition[] => {
  return conditions.map((c) => {
    const conditionDOs: ConditionDataOutput[] = dataOutputs.map((doItem) => {
      const matchingCdo = c.data_outputs.find((cdo) => cdo.output_id === doItem.output_id);
      return (
        matchingCdo ?? {
          output_id: doItem.output_id,
          child_data_output: DEFAULT_DYNAMIC_DATA_INPUT,
          data_input: DEFAULT_DYNAMIC_DATA_INPUT,
        }
      );
    });
    return { ...c, data_outputs: conditionDOs };
  });
};

const ConditionalFields = ({ step }: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });

  const ConditionalFieldsUI = (condition: Condition, idx: number) => (
    <ConditionFields
      key={condition.condition_id}
      condition={condition}
      onChangeNoDelay={(c: Condition) => {
        const updated = fields.conditions.map((input) =>
          input.condition_id === c.condition_id ? c : input,
        );
        onChangeNoDelay({ conditions: updated });
      }}
      onChangeDelay={(c: Condition) => {
        const updated = fields.conditions.map((input) =>
          input.condition_id === c.condition_id ? c : input,
        );
        onChange({ conditions: updated });
      }}
      onDelete={() => {
        const updated = fields.conditions.filter((c) => c.condition_id !== condition.condition_id);
        onChangeNoDelay({ conditions: updated });
      }}
      step={step}
      conditionIndex={idx}
      dataOutputs={fields.data_outputs}
    />
  );

  return (
    <Stack>
      <Divider mt="xs" mb={'0'} />
      <span>
        <StyledInputLabel>Outputs</StyledInputLabel>
        <Stack>
          {fields.data_outputs.map((conditionalDataOutput) => (
            <ConditionalDataOutputFields
              key={conditionalDataOutput.output_id}
              conditionalDataOutput={conditionalDataOutput}
              onChangeDelay={(updatedInput: ConditionalDataOutput) => {
                const updatedOutputs = fields.data_outputs.map((input) =>
                  input.output_id === updatedInput.output_id ? updatedInput : input,
                );
                // also update conditions to match data outputs
                onChange({
                  data_outputs: updatedOutputs,
                  conditions: updateConditionToMatchOutputs(updatedOutputs, fields.conditions),
                });
              }}
              onChangeNoDelay={(updatedInput: ConditionalDataOutput) => {
                const updatedOutputs = fields.data_outputs.map((input) =>
                  input.output_id === updatedInput.output_id ? updatedInput : input,
                );
                onChangeNoDelay({ data_outputs: updatedOutputs });
              }}
              onDelete={() => {
                const updatedOutputs = fields.data_outputs.filter(
                  (input) => input.output_id !== conditionalDataOutput.output_id,
                );
                // also update conditions to match data outputs
                onChangeNoDelay({
                  data_outputs: updatedOutputs,
                  conditions: updateConditionToMatchOutputs(updatedOutputs, fields.conditions),
                });
              }}
              step={step}
            />
          ))}

          <StepFieldsAddButton
            stepId={step.step_id}
            elementCount={fields.data_outputs.length}
            onClick={() => {
              const newInput: ConditionalDataOutput = { output_id: generateUid(), name: '' };
              onChangeNoDelay({ data_outputs: [...fields.data_outputs, newInput] });
            }}
          />
        </Stack>
      </span>
      <Divider mt="xs" mb={'0'} />
      <span>
        <Stack mb={'xs'} gap={'7px'}>
          <Group gap={'7px'}>
            <Badge
              color="brandBlue.6"
              variant={'light'}
              style={{ borderRadius: '20%', paddingLeft: '6px', paddingRight: '6px' }}
            >
              IF
            </Badge>
            <StyledInputLabel mb={0}>Path Conditions</StyledInputLabel>
          </Group>
          <Text c={'dimmed'} size={'12px'}>
            Define multiple paths based on specific criteria
          </Text>
        </Stack>
        <Stack>
          {fields.conditions
            .filter((c) => c.condition_id !== ELSE_CLAUSE_ID)
            .map((condition, idx) => ConditionalFieldsUI(condition, idx))}

          <StepFieldsAddButton
            stepId={step.step_id}
            elementCount={fields.conditions.length}
            onClick={() => {
              const newCondition: Condition = {
                condition_id: generateUid(),
                name: '',
                condition_data_inputs: [],
                condition_logic_source: InputSource.UserSpecified,
                condition_logic_data_inputs: [],
                condition_logic_description: '',
                control_logic: makeDecisionControlLogic(),
                action: ConditionAction.RaiseError,
                children: [],
                data_outputs: [],
              };
              if (fields.conditions.length === 0) {
                onChangeNoDelay({ conditions: [newCondition] });
              } else {
                const conds = fields.conditions;
                const lastCondition = conds[conds.length - 1];
                const beforeLast = conds.slice(0, -1);
                onChangeNoDelay({
                  conditions: [...beforeLast, newCondition, lastCondition],
                });
              }
            }}
          />
        </Stack>
      </span>
      <span>
        <Stack mb={'xs'} gap={'7px'}>
          <Group gap={'7px'}>
            <Badge
              color="#757575"
              variant={'light'}
              style={{ borderRadius: '20%', paddingLeft: '6px', paddingRight: '6px' }}
            >
              ELSE
            </Badge>
            <StyledInputLabel mb={0}>Path Condition</StyledInputLabel>
          </Group>
          <Text c={'dimmed'} size={'12px'}>
            Define what happens when no other conditions are met
          </Text>
        </Stack>

        <Stack>
          {fields.conditions
            .filter((c) => c.condition_id === ELSE_CLAUSE_ID)
            .map((condition) => ConditionalFieldsUI(condition, fields.conditions.length - 1))}
        </Stack>
      </span>
    </Stack>
  );
};

export default ConditionalFields;
